// Shared cart math - single source of truth for price/stock calculations
// that used to be reimplemented separately in CartContext, CartItem, and
// MenuItemCard. Mirrors the Zimzo mobile app's (and the backend's) formulas
// exactly so client-side estimates never drift from what the server charges.

const LOW_STOCK_DELIVERY_FEE = 30;
const STANDARD_DELIVERY_FEE = 20;
const LOW_STOCK_THRESHOLD = 499; // subtotal threshold, matches orderController.js

/** A cart item's unit price, including any selected customization modifiers. */
export const getItemUnitPrice = (item) => {
  if (typeof item.itemTotalPrice === 'number') return item.itemTotalPrice;

  const base = item.price || 0;
  const modifiers = (item.customizations || []).reduce(
    (sum, c) => sum + (c.priceModifier || 0),
    0
  );
  return base + modifiers;
};

export const getItemLineTotal = (item) => getItemUnitPrice(item) * item.quantity;

/** Groups flat cart items by storeId, preserving first-seen order. */
export const groupCartByStore = (cartItems) => {
  const order = [];
  const byStore = new Map();

  for (const item of cartItems) {
    const storeId = item.storeId || 'unknown';
    if (!byStore.has(storeId)) {
      byStore.set(storeId, []);
      order.push(storeId);
    }
    byStore.get(storeId).push(item);
  }

  return order.map((storeId) => ({ storeId, items: byStore.get(storeId) }));
};

const storeDeliveryFee = (storeSubtotal, deliveryMethod) =>
  deliveryMethod !== 'delivery' ? 0 : storeSubtotal < LOW_STOCK_THRESHOLD ? LOW_STOCK_DELIVERY_FEE : STANDARD_DELIVERY_FEE;

/**
 * Computes cart totals exactly the way orderController.js does: per-store
 * subtotal/packaging, a per-store delivery-fee quote, and a single combined
 * delivery fee equal to the MAX across stores (never summed).
 *
 * `storeMinimums` is a { [storeId]: minimumOrderValue } lookup - each
 * store's own Super Admin-set minimum (stores/{storeId}.minimumOrderValue,
 * see multi-tenant-plan.md §16), not a platform-wide constant. A store
 * missing from the lookup (or with no value set) defaults to 0 - no
 * minimum.
 */
export const computeCartTotals = (cartItems, deliveryMethod, storeMinimums = {}) => {
  const groups = groupCartByStore(cartItems).map(({ storeId, items }) => {
    const subtotal = items.reduce((sum, item) => sum + getItemLineTotal(item), 0);
    const packagingCharge = items.reduce(
      (sum, item) => sum + (item.packagingCharge || 0) * item.quantity,
      0
    );
    const minimumOrderValue = storeMinimums[storeId] || 0;
    return {
      storeId,
      items,
      subtotal,
      packagingCharge,
      minimumOrderValue,
      belowMinimum: subtotal < minimumOrderValue,
      deliveryFee: storeDeliveryFee(subtotal, deliveryMethod)
    };
  });

  const subtotal = groups.reduce((sum, g) => sum + g.subtotal, 0);
  const packagingCharge = groups.reduce((sum, g) => sum + g.packagingCharge, 0);
  const deliveryFee = groups.length ? Math.max(...groups.map((g) => g.deliveryFee)) : 0;
  const total = subtotal + packagingCharge + deliveryFee;

  return { groups, subtotal, packagingCharge, deliveryFee, total };
};

/** How many more of a product can be added, given current inventory and what's already in the cart. */
export const getAvailableQuantity = (inventoryMap, cartItems, productId) => {
  const currentStock = inventoryMap?.[productId]?.currentStock || 0;
  const inCart = cartItems.reduce((sum, item) => {
    const itemProductId = item.productId || item.id;
    return itemProductId === productId ? sum + item.quantity : sum;
  }, 0);
  return Math.max(0, currentStock - inCart);
};
